import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  Button,
  CircularProgress,
  Collapse,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Transcribe as TranscriptsIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  MenuBook as KnowledgeBaseIcon,
  People as PeopleIcon,
  History as HistoryIcon,
  CreditCard as SubscriptionIcon,
  SupportAgent as SupportIcon,
  CalendarToday as CalendarIcon,
  School as TrainingIcon,
  Phone as LiveCallIcon,
  ExpandLess,
  ExpandMore,
  Insights as InsightsIcon,
  Business as OrganizationIcon,
  AssessmentOutlined as AssessmentIcon,
  Feedback as FeedbackIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';
import { useAppDispatch } from '../hooks/redux';
import { logoutUser } from '../store/authSlice';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { useFeatures } from '../hooks/useFeatures';
import { apiService } from '../services/apiService';
import { frontendConfig, appConfig } from '../config';

const AppleIcon: React.FC<{ fontSize?: number }> = ({ fontSize = 20 }) => (
  <svg width={fontSize} height={fontSize} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
  </svg>
);

const WindowsIcon: React.FC<{ fontSize?: number }> = ({ fontSize = 20 }) => (
  <svg width={fontSize} height={fontSize} viewBox="0 0 88 88" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 12.402l35.687-4.86.016 34.423-35.67.203zm35.67 33.529l.028 34.453L.028 75.48.026 45.7zm4.326-39.025L87.314 0v41.527l-47.318.376zm47.329 39.349l-.011 41.34-47.318-6.678-.066-34.739z" />
  </svg>
);

const drawerWidth = 260;

interface SidebarProps {
  mobileOpen: boolean;
  handleDrawerToggle: () => void;
  userEmail?: string;
  onFeedbackClick?: () => void;
}

interface NavSubItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  requiredRole: 'any' | 'admin';
  isSSO?: boolean;
  tutorialId?: string;
}

interface NavSection {
  text: string;
  icon: React.ReactNode;
  isCollapsible: boolean;
  subItems?: NavSubItem[];
  path?: string;
  requiredRole?: 'any' | 'admin';
  isSSO?: boolean;
  isExternal?: boolean;
  tutorialId?: string;
}

// Helper to determine which section contains a given path
const getSectionForPath = (pathname: string): string | null => {
  const pathToSection: Record<string, string> = {
    '/dashboard': 'Insights',
    '/sessions': 'Insights',
    '/knowledge-base': 'Knowledge',
    '/training': 'Knowledge',
    '/users': 'Settings',
    '/subscription': 'Settings',
    '/calendar': 'Settings',
    '/settings': 'Settings',
  };
  return pathToSection[pathname] || null;
};

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, handleDrawerToggle, userEmail, onFeedbackClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { canView, isSuperAdmin } = useRoleAccess();
  // Capability flags — Lushin runs post-call-only, so Knowledge Base / Training nav
  // entries are hidden until their feature is enabled. (Live Call is always shown.)
  const { liveIntelligence, training, livePlaybook } = useFeatures();
  const [ssoLoading, setSsoLoading] = useState(false);

  // Initialize open section based on current path
  const getInitialOpenSections = (): Record<string, boolean> => {
    const currentSection = getSectionForPath(location.pathname);
    return {
      'Insights': currentSection === 'Insights',
      'Knowledge': currentSection === 'Knowledge',
      'Settings': currentSection === 'Settings',
    };
  };

  const [openSections, setOpenSections] = useState<Record<string, boolean>>(getInitialOpenSections);

  // Sync open section with current path when location changes
  useEffect(() => {
    const currentSection = getSectionForPath(location.pathname);
    if (currentSection) {
      setOpenSections({
        'Insights': currentSection === 'Insights',
        'Knowledge': currentSection === 'Knowledge',
        'Settings': currentSection === 'Settings',
      });
    }
  }, [location.pathname]);

  // Define navigation structure with collapsible sections
  const baseSections: NavSection[] = [
    {
      text: 'Insights',
      icon: <InsightsIcon />,
      isCollapsible: true,
      tutorialId: 'insights-section',
      subItems: [
        {
          text: 'Analytics',
          icon: <AssessmentIcon />,
          path: '/dashboard',
          requiredRole: 'any',
        },
        {
          text: 'Sessions',
          icon: <HistoryIcon />,
          path: '/sessions',
          requiredRole: 'any',
          tutorialId: 'sessions-link',
        },
        {
          text: 'Upload Sessions',
          icon: <UploadIcon />,
          path: '/sessions/upload',
          requiredRole: 'admin',
        },
      ],
    },
    {
      text: 'Live Call',
      icon: <LiveCallIcon />,
      isCollapsible: false,
      path: '/sso-redirect',
      requiredRole: 'any',
      isSSO: true,
      tutorialId: 'live-call-button',
    },
    {
      text: 'Knowledge',
      icon: <KnowledgeBaseIcon />,
      isCollapsible: true,
      tutorialId: 'knowledge-section',
      subItems: [
        {
          text: 'Knowledge Base',
          icon: <KnowledgeBaseIcon />,
          path: '/knowledge-base',
          requiredRole: 'admin',
          tutorialId: 'knowledge-base-link',
        },
        {
          text: 'Training',
          icon: <TrainingIcon />,
          path: '/training',
          requiredRole: 'any',
          tutorialId: 'training-link',
        },
      ],
    },
    {
      text: 'Support Tickets',
      icon: <SupportIcon />,
      isCollapsible: false,
      path: '/support-tickets',
      requiredRole: 'any',
    },
    {
      text: 'Settings',
      icon: <SettingsIcon />,
      isCollapsible: true,
      subItems: [
        {
          text: 'Users',
          icon: <PeopleIcon />,
          path: '/users',
          requiredRole: 'admin',
        },
        {
          text: 'Subscription',
          icon: <SubscriptionIcon />,
          path: '/subscription',
          requiredRole: 'admin',
        },
        {
          text: 'Calendar',
          icon: <CalendarIcon />,
          path: '/calendar',
          requiredRole: 'any',
          tutorialId: 'calendar-link',
        },
        {
          text: 'Organization Settings',
          icon: <OrganizationIcon />,
          path: '/settings',
          requiredRole: 'any',
          tutorialId: 'settings-link',
        },
      ],
    },
  ];

  // Apply capability gating: hide the Knowledge Base + Training nav entries when
  // those features are disabled (Lushin's post-call-only default). Empty
  // collapsible sections are dropped entirely. Live Call is always shown — it opens
  // the web frontend to run/record calls; analysis happens post-call.
  const navigationSections: NavSection[] = baseSections
    .map((section) => {
      if (section.text === 'Knowledge' && section.subItems) {
        const subItems = section.subItems.filter((si) => {
          // Knowledge Base hosts two different models — the old objection/FAQ/
          // landmine set and the live discovery checklist — so either capability
          // makes it worth showing. The page hides the tabs that don't apply.
          if (si.path === '/knowledge-base') return liveIntelligence || livePlaybook;
          if (si.path === '/training') return training;
          return true;
        });
        return { ...section, subItems };
      }
      return section;
    })
    .filter(
      (section) =>
        !(section.isCollapsible && section.subItems && section.subItems.length === 0)
    );

  const toggleSection = (sectionText: string) => {
    setOpenSections(prev => {
      const isCurrentlyOpen = prev[sectionText];
      // Close all sections, then toggle the clicked one (accordion behavior)
      const allClosed: Record<string, boolean> = {};
      Object.keys(prev).forEach(key => {
        allClosed[key] = false;
      });
      return {
        ...allClosed,
        [sectionText]: !isCurrentlyOpen,
      };
    });
  };

  const isSubItemActive = (section: NavSection): boolean => {
    if (!section.subItems) return false;
    return section.subItems.some(item => location.pathname === item.path);
  };

  const handleNavigation = (path: string, isSSO?: boolean, closeMobileDrawer: boolean = true, isExternal?: boolean) => {
    if (isSSO) {
      handleSSORedirect();
    } else if (isExternal) {
      window.open(path, '_blank', 'noopener,noreferrer');
    } else {
      navigate(path);
      if (mobileOpen && closeMobileDrawer) {
        handleDrawerToggle();
      }
    }
  };

  const handleSubItemClick = (e: React.MouseEvent, path: string, isSSO?: boolean) => {
    e.stopPropagation();
    handleNavigation(path, isSSO, mobileOpen);
  };

  const handleSSORedirect = async () => {
    try {
      setSsoLoading(true);

      // First verify user has access to frontend (checks subscription + authorizedUsers)
      const verifyResponse = await apiService.get('/admin/verify-access');

      if (!verifyResponse.success) {
        throw new Error(verifyResponse.message || 'Access verification failed');
      }

      // User is authorized, generate SSO token
      const response = await apiService.post('/auth/generate-sso-token');

      if (response.success && response.data?.ssoToken) {
        const ssoToken = response.data.ssoToken;

        // Open frontend in new tab with SSO token
        window.open(`${frontendConfig.baseURL}/sso?token=${ssoToken}`, '_blank');
      }
    } catch (error: any) {
      console.error('SSO redirect failed:', error);

      // Extract error code from the error object
      // Backend returns: { success: false, message: '...', error: { code: '...', message: '...' } }
      // apiService throws: { success: false, error: { code: '...', message: '...' }, message: '...' }
      const errorCode = error?.error?.code || error?.response?.data?.error?.code;
      const backendMessage = error?.message || error?.response?.data?.message;

      let errorMessage = 'Failed to access Live Call. Please try again.';

      switch (errorCode) {
        case 'USER_NOT_AUTHORIZED':
          errorMessage = 'You are not authorized to access Live Call. Please contact your administrator to request access.';
          break;
        case 'USER_NOT_REGISTERED':
          errorMessage = 'Your account is not registered in the system. Please contact your administrator.';
          break;
        case 'NO_SUBSCRIPTION':
          errorMessage = 'No active subscription found. Please contact your administrator to set up a subscription.';
          break;
        case 'SUBSCRIPTION_EXPIRED':
          errorMessage = 'Your subscription has expired. Please contact your administrator to renew.';
          break;
        case 'SUBSCRIPTION_GRACE_PERIOD_ENDED':
          errorMessage = 'Your subscription grace period has ended. Please contact your administrator to renew.';
          break;
        case 'USER_DISABLED':
          errorMessage = 'Your account has been deactivated. Please contact your administrator.';
          break;
        case 'ORGANIZATION_NOT_FOUND':
          errorMessage = 'Organization not found. Please contact support.';
          break;
        case 'CONFIGURATION_ERROR':
          errorMessage = 'Server configuration error. Please contact support.';
          break;
        case 'AUTH_CHECK_FAILED':
          errorMessage = 'Unable to verify authorization.';
          break;
        default:
          // Use backend message if available, otherwise use generic message
          if (backendMessage && backendMessage !== 'undefined') {
            errorMessage = backendMessage;
          }
          break;
      }

      console.error('Live Call access error:', { errorCode, backendMessage, errorMessage });
      alert(errorMessage);
    } finally {
      setSsoLoading(false);
    }
  };

  const handleLogout = () => {
    dispatch(logoutUser());
  };

  // Super admins only see the Knowledge Base section
  const visibleNavigationSections: NavSection[] = isSuperAdmin
    ? [
        {
          text: 'Knowledge',
          icon: <KnowledgeBaseIcon />,
          isCollapsible: true,
          subItems: [
            {
              text: 'Knowledge Base',
              icon: <KnowledgeBaseIcon />,
              path: '/knowledge-base',
              requiredRole: 'admin',
            },
          ],
        },
      ]
    : navigationSections;

  const drawer = (
    <Box className="h-full flex flex-col">
      <Box className="p-4">
        <Box className="flex justify-center">
          <img
            src="/sandler-lushin-logo.svg"
            alt="Sandler Lushin"
            style={{ maxWidth: '180px', height: 'auto' }}
          />
        </Box>
        <Typography variant="body2" color="textSecondary" className="text-center" sx={{ mt: 1 }}>
          {appConfig.name}
        </Typography>
      </Box>

      <Divider />

      <Box className="flex-1 overflow-y-auto">
        <List>
          {visibleNavigationSections.map((section) => {
            // For non-collapsible items (direct links)
            if (!section.isCollapsible) {
              if (section.requiredRole && !canView(section.requiredRole)) return null;
              return (
                <ListItem key={section.text} disablePadding>
                  <ListItemButton
                    selected={location.pathname === section.path}
                    onClick={() => handleNavigation(section.path!, section.isSSO, true, section.isExternal)}
                    className="mx-2 rounded-lg"
                    disabled={ssoLoading && section.isSSO}
                    data-tutorial={section.tutorialId}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {ssoLoading && section.isSSO ? <CircularProgress size={20} /> : section.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={section.text}
                      slotProps={{ primary: { fontSize: '0.875rem' } }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            }

            // For collapsible sections
            const visibleSubItems = section.subItems?.filter(item => canView(item.requiredRole)) || [];
            if (visibleSubItems.length === 0) return null;

            const isOpen = openSections[section.text];
            const hasActiveSubItem = isSubItemActive(section);

            return (
              <React.Fragment key={section.text}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => toggleSection(section.text)}
                    className="mx-2 rounded-lg"
                    data-tutorial={section.tutorialId}
                    sx={{
                      backgroundColor: hasActiveSubItem ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>{section.icon}</ListItemIcon>
                    <ListItemText
                      primary={section.text}
                      slotProps={{
                        primary: {
                          fontSize: '0.875rem',
                          fontWeight: hasActiveSubItem ? 600 : 400,
                        },
                      }}
                    />
                    {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {visibleSubItems.map((subItem) => (
                      <ListItem key={subItem.text} disablePadding>
                        <ListItemButton
                          selected={location.pathname === subItem.path}
                          onClick={(e) => handleSubItemClick(e, subItem.path, subItem.isSSO)}
                          className="mx-2 rounded-lg"
                          sx={{ pl: 4, py: 0.75 }}
                          data-tutorial={subItem.tutorialId}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>{subItem.icon}</ListItemIcon>
                          <ListItemText
                            primary={subItem.text}
                            slotProps={{ primary: { fontSize: '0.8125rem' } }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          })}
        </List>
      </Box>

      <Divider />

      <Box className="p-4">
        {/* App Download Icons */}
        <Typography
          variant="body2"
          className="block text-center mb-2"
          sx={{ fontWeight: 700, fontSize: '0.925rem', color: 'primary.main' }}
        >
          Download Desktop Versions:
        </Typography>
        <Box className="flex justify-center gap-4 mb-3">
          <Box
            component="a"
            href="https://apps.apple.com/us/app/apexia-labs/id6760661143"
            target="_blank"
            rel="noopener noreferrer"
            title="Download Mac App"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
              transition: 'color 0.2s',
            }}
          >
            <AppleIcon fontSize={33} />
          </Box>
          <Box
            component="a"
            href="https://d3dg1lmpu1jt4g.cloudfront.net/LushinSetup-3.0.0.exe"
            target="_blank"
            rel="noopener noreferrer"
            title="Download Windows App"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
              transition: 'color 0.2s',
            }}
          >
            <WindowsIcon fontSize={33} />
          </Box>
        </Box>

        {/* Feedback Button */}
        {onFeedbackClick && (
          <Button
            fullWidth
            variant="outlined"
            startIcon={<FeedbackIcon />}
            onClick={onFeedbackClick}
            sx={{ textTransform: 'none', mb: 2 }}
          >
            Feedback
          </Button>
        )}

        <Button
          fullWidth
          variant="outlined"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          color="error"
        >
          Logout
        </Button>

        {/* User Email */}
        {userEmail && (
          <Box className="mt-3 px-2 text-center">
            <Typography
              variant="caption"
              color="textSecondary"
              className="block text-xs"
            >
              Logged in as
            </Typography>
            <Typography
              variant="body2"
              className="truncate font-medium"
              title={userEmail}
            >
              {userEmail}
            </Typography>
          </Box>
        )}

        <Typography
          variant="caption"
          color="textSecondary"
          className="block text-center mt-2"
        >
          v{appConfig.version}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          // Keep the permanent sidebar below app overlays (modals/dialogs) so it
          // never paints over a centered popup. MUI's default drawer z-index
          // (1200) sits above Tailwind overlays; drop it under the appBar layer.
          zIndex: (theme) => theme.zIndex.appBar,
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            zIndex: (theme) => theme.zIndex.appBar,
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;